import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listMyRequests } from "../data/requests";
import { submitReview } from "../data/reviews";
import { getRequestStatusMeta } from "../lib/requestStatus";
import { toId } from "../lib/subjects";
import { toast } from "./components/ToastHost";

const ACTIVE_STATUSES = new Set(["NEW", "CHAT_ACTIVE"]);
const RATEABLE_STATUSES = new Set(["TRIAL_DONE", "BECAME_CLIENT", "CLOSED"]);

function safeTs(iso) {
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function relativeTime(iso, t) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("common.justNow");
  if (mins < 60) return t("common.minutesAgo_other", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("common.hoursAgo_other", { count: hrs });
  const days = Math.floor(hrs / 24);
  return t("common.daysAgo_other", { count: days });
}

/* ── Status badge ────────────────────────────────────────────────── */
function StatusBadge({ status, t }) {
  const { text, tone } = getRequestStatusMeta(t, status);
  const cls =
    tone === "success"
      ? "bg-[#98dea3]/15 text-[#98dea3] ring-[#98dea3]/20"
      : "bg-white/8 text-white/70 ring-white/10";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ring-1 ${cls}`}>
      {text}
    </span>
  );
}

/* ── Mode pill ───────────────────────────────────────────────────── */
function ModePill({ mode, t }) {
  const raw = String(mode || "").toUpperCase();
  const label =
    raw === "ONLINE"
      ? t("request.mode.ONLINE")
      : raw === "OFFLINE"
        ? t("request.mode.OFFLINE")
        : raw === "BOTH"
          ? t("request.mode.BOTH")
          : mode || "—";
  return <span className="text-xs text-white/50">{label}</span>;
}

/* ── Star picker ─────────────────────────────────────────────────── */
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          className="text-2xl leading-none transition-transform hover:scale-110"
          style={{ color: n <= (hovered || value) ? "#fffd74" : "rgba(255,255,255,0.2)" }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/* ── Review modal ────────────────────────────────────────────────── */
function ReviewModal({ open, requestId, tutorName, t, onClose, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      await submitReview({ requestId, stars, comment });
      toast("✅ " + t("bookings.actions.rateLesson", { defaultValue: "Дякуємо за оцінку!" }));
      onSubmitted();
    } catch (e) {
      toast("❌ " + (e?.message || "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl bg-[#32384a] p-6 ring-1 ring-white/10 shadow-2xl">
        <h2 className="font-unbounded text-lg font-bold text-white">
          {t("bookings.actions.rateLesson", { defaultValue: "Оцінити заняття" })}
        </h2>
        <p className="mt-1 text-sm text-white/55">{tutorName}</p>

        <div className="mt-5 flex justify-center">
          <StarPicker value={stars} onChange={setStars} />
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("bookings.dispute.descriptionPlaceholder", { defaultValue: "Залиште коментар (необов'язково)…" })}
          rows={3}
          className="mt-4 w-full rounded-xl bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 ring-1 ring-white/10 focus:outline-none focus:ring-[#7269e3] resize-none"
        />

        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/70 hover:bg-white/5"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy || !stars}
            className="rounded-xl bg-[#7269e3] px-5 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("actions.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Single request card ─────────────────────────────────────────── */
function RequestCard({ r, t, navigate, onRate }) {
  const status = String(r.status || "NEW").toUpperCase();
  const isActive = ACTIVE_STATUSES.has(status);
  const isChatOpen = status === "CHAT_ACTIVE";
  const canRate = RATEABLE_STATUSES.has(status) && !r.review;

  const tutorName = r.tutorName || t("common.tutor");
  const subjectKey = toId(r.subject);
  const subjectLabel = t(`subjects.${subjectKey}`, { defaultValue: r.subject || "—" });
  const kindLabel = r.packageTitle
    ? t("packages.requestLabel", { title: r.packageTitle, price: Number(r.packagePrice || 0).toLocaleString("uk-UA") })
    : r.isFreeIntro
    ? t("request.kind.INTRO")
    : String(r.kind || "TRIAL").toUpperCase() === "TRIAL"
      ? (Number(r.durationMin) > 0
          ? t("request.consultDuration", { min: Number(r.durationMin) })
          : t("request.kind.TRIAL"))
      : t("request.kind.REGULAR");

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
      <div className="p-5">
        {/* Top row */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-white truncate">{tutorName}</span>
              <StatusBadge status={status} t={t} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-white/60">
              <span>{subjectLabel}</span>
              <span className="text-white/30">•</span>
              <span>{kindLabel}</span>
              <span className="text-white/30">•</span>
              <ModePill mode={r.mode} t={t} />
            </div>
          </div>
          <span className="text-xs text-white/40 shrink-0 mt-0.5">
            {relativeTime(r.createdAt, t)}
          </span>
        </div>

        {/* Message preview */}
        {r.message ? (
          <p className="mt-3 text-sm text-white/70 line-clamp-2 leading-relaxed">
            {r.message}
          </p>
        ) : null}

        {/* Chat-open hint */}
        {isChatOpen && (
          <div className="mt-3 rounded-xl bg-[#98dea3]/10 ring-1 ring-[#98dea3]/20 px-3.5 py-2.5 text-xs text-[#98dea3]">
            {t("bookings.activeRequestsHint")}
          </div>
        )}

        {/* Status hint for NEW — this page is client-only (tutors are
            redirected away), so use the client-facing wording: the client
            waits for the specialist to accept, they don't "open" the chat. */}
        {status === "NEW" && (
          <div className="mt-3 rounded-xl bg-[#7269e3]/10 ring-1 ring-[#7269e3]/20 px-3.5 py-2.5 text-xs text-[#a09de8]">
            {t("request.details.stageNewStudent")}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-white/[0.06] px-5 py-3 flex flex-wrap items-center gap-2">
        <Link
          to={`/requests/${r.id}`}
          className="rounded-xl border border-white/15 bg-white/5 px-4 py-1.5 text-sm hover:bg-white/10 transition-colors"
        >
          {t("actions.details")}
        </Link>

        {isChatOpen && (
          <button
            onClick={() => navigate(`/chat/${r.tutorId}?name=${encodeURIComponent(tutorName)}`)}
            className="rounded-xl bg-[#7269e3] px-4 py-1.5 text-sm font-medium hover:brightness-110 transition-all"
          >
            {t("actions.openChat")}
          </button>
        )}

        {r.meetingUrl && (
          <a
            href={r.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl bg-[#98dea3] px-4 py-1.5 text-sm font-bold text-[#141826] hover:brightness-105 transition-all"
          >
            🎥 {t("request.details.joinCall", { defaultValue: "Приєднатись до дзвінка" })}
          </a>
        )}

        {canRate && (
          <button
            onClick={() => onRate(r)}
            style={{ background: "rgba(255,253,116,0.12)", color: "#fffd74", border: "1px solid rgba(255,253,116,0.25)" }}
            className="rounded-xl px-4 py-1.5 text-sm font-medium hover:brightness-110 transition-all"
          >
            ★ {t("bookings.actions.rateLesson", { defaultValue: "Оцінити" })}
          </button>
        )}

        {r.review && (
          <span className="text-xs text-white/35">
            {"★".repeat(r.review.stars)}{"☆".repeat(5 - r.review.stars)}
          </span>
        )}

        {!isActive && (
          <span className="ml-auto text-xs text-white/35">
            {r.closedAt ? relativeTime(r.closedAt, t) : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────── */
export default function MyRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const auth = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("auth") || "null"); } catch { return null; }
  }, []);
  const isTutor = (auth?.role || "").toUpperCase() === "TUTOR";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active");
  const [reviewModal, setReviewModal] = useState({ open: false, request: null });

  const load = useCallback(async () => {
    if (isTutor) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const data = await listMyRequests();
      const sorted = [...data].sort(
        (a, b) => safeTs(b.updatedAt || b.createdAt) - safeTs(a.updatedAt || a.createdAt)
      );
      setItems(sorted);
    } catch (e) {
      console.error(e);
      setError(t("bookings.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t, isTutor]);

  useEffect(() => { load(); }, [load]);

  const { active, history } = useMemo(() => {
    const active = items.filter((r) => ACTIVE_STATUSES.has(String(r.status || "").toUpperCase()));
    const history = items.filter((r) => !ACTIVE_STATUSES.has(String(r.status || "").toUpperCase()));
    return { active, history };
  }, [items]);

  const visible = tab === "active" ? active : history;

  if (isTutor) return <Navigate to="/dashboard/tutor" replace />;

  return (
    <>
      <ReviewModal
        open={reviewModal.open}
        requestId={reviewModal.request?.id}
        tutorName={reviewModal.request?.tutorName || t("common.tutor")}
        t={t}
        onClose={() => setReviewModal({ open: false, request: null })}
        onSubmitted={() => {
          setReviewModal({ open: false, request: null });
          load();
        }}
      />

      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-3xl px-4 pt-10 pb-24">

          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-unbounded text-2xl font-extrabold tracking-tight">
                {t("navbar.myRequests", { defaultValue: "Мої запити" })}
              </h1>
              <p className="mt-1 text-sm text-white/55">
                {t("bookings.subtitleStudent")}
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-50 transition-colors"
            >
              {loading ? t("common.loading") : t("actions.refresh")}
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 rounded-xl bg-white/5 ring-1 ring-white/10 p-1">
            {[
              { key: "active", label: t("bookings.tabs.activeRequests"), count: active.length },
              { key: "history", label: t("bookings.tabs.history"), count: history.length },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  tab === key ? "bg-[#7269e3] text-white" : "text-white/60 hover:text-white/80"
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`ml-2 rounded-full px-1.5 py-0.5 text-xs ${
                    tab === key ? "bg-white/20 text-white" : "bg-white/10 text-white/50"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="mt-5">
            {loading ? (
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-8 text-center text-white/50">
                {t("common.loading")}
              </div>
            ) : error ? (
              <div className="rounded-2xl bg-red-500/10 ring-1 ring-red-500/20 p-6 text-center">
                <p className="text-red-300 text-sm">{error}</p>
                <button onClick={load} className="mt-3 rounded-xl bg-white/10 px-4 py-2 text-sm hover:bg-white/15">
                  {t("common.retry")}
                </button>
              </div>
            ) : visible.length === 0 ? (
              <EmptyState tab={tab} t={t} navigate={navigate} />
            ) : (
              <div className="space-y-3">
                {visible.map((r) => (
                  <RequestCard
                    key={r.id}
                    r={r}
                    t={t}
                    navigate={navigate}
                    onRate={(r) => setReviewModal({ open: true, request: r })}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

/* ── Empty state ─────────────────────────────────────────────────── */
function EmptyState({ tab, t, navigate }) {
  if (tab === "history") {
    return (
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-10 text-center">
        <p className="text-white/70 font-medium">{t("bookings.emptyPast.title")}</p>
        <p className="mt-2 text-sm text-white/45">{t("bookings.emptyPast.subtitle")}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-10 text-center">
      <p className="text-white/70 font-medium">{t("bookings.emptyRequests.title")}</p>
      <p className="mt-2 text-sm text-white/45">{t("bookings.emptyRequests.subtitle")}</p>
      <button
        onClick={() => navigate("/search")}
        className="mt-5 rounded-xl bg-[#7269e3] px-5 py-2.5 text-sm font-medium hover:brightness-110 transition-all"
      >
        {t("actions.findTutor")}
      </button>
    </div>
  );
}
